import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { DatePicker } from "@mantine/dates";
import { Popover } from "@mantine/core";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import PlaceIcon from "@mui/icons-material/Place";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Nav from "../NavBar/Nav";
import FooterNew from "../NavBar/FooterNew";
import base_url from "../../api/bootapi";
import { useExperienceCart } from "./ExperienceCartContext";
import ExperienceProductCard from "./ExperienceProductCard";
import {
  cacheProduct,
  getRating,
  getFromPrice,
  formatDate,
  formatDateRange,
} from "./experienceUtils";
import "./Experiences.css";

const DURATION_OPTIONS = [
  { key: "under2", label: "Up to 2 hours", test: (mins) => mins != null && mins <= 120 },
  { key: "2to4", label: "2 - 4 hours", test: (mins) => mins != null && mins > 120 && mins <= 240 },
  { key: "4to24", label: "4 hours - 1 day", test: (mins) => mins != null && mins > 240 && mins <= 1440 },
  { key: "multi", label: "Multi-day", test: (mins) => mins != null && mins > 1440 },
];

const PAGE_SIZE = 24;

// Builds a compact page list like [1, 2, 3, '...', 263] - always keeps the
// first page, the last page, and a small window around the current page,
// collapsing everything else into a single '...' gap.
function buildPageList(current, total) {
  if (total <= 1) return [1];
  const pages = new Set([1, total, current]);
  if (current - 1 > 1) pages.add(current - 1);
  if (current + 1 < total) pages.add(current + 1);

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];
  sorted.forEach((page, idx) => {
    if (idx > 0 && page - sorted[idx - 1] > 1) result.push("...");
    result.push(page);
  });
  return result;
}

const RATING_OPTIONS = [4, 3];

// Quick category chips shown under the filter bar. Since the destinations
// API doesn't expose Viator's tag taxonomy to us, these filter by matching
// the chip's keywords against each product's title/description — a
// lightweight client-side approximation of the reference design's category
// filters rather than a true tag-based filter.
const CATEGORY_CHIPS = [
  { key: "tours", label: "Tours, Sightseeing & Cruises", keywords: ["tour", "sightseeing", "cruise", "boat"] },
  { key: "outdoor", label: "Outdoor Activities", keywords: ["outdoor", "hike", "safari", "adventure", "desert"] },
  { key: "luxury", label: "Private and Luxury", keywords: ["private", "luxury", "vip"] },
  { key: "sightseeing", label: "Sightseeing", keywords: ["sightseeing", "landmark", "monument"] },
  { key: "food", label: "Food & Drink", keywords: ["food", "dining", "wine", "tasting", "culinary"] },
  { key: "water", label: "Water Sports", keywords: ["diving", "snorkel", "kayak", "water", "beach"] },
];

export default function ExperienceResults() {
  const navigate = useNavigate();
  const location = useLocation();
  const { count: cartCount } = useExperienceCart();

  const params = new URLSearchParams(location.search);
  const destinationFromState = location.state && location.state.destination;

  const destination =
    destinationFromState || {
      destination_id: params.get("destination_id"),
      name: params.get("name") || "your destination",
      default_currency_code: params.get("currency") || "USD",
    };

  const [destinationQuery, setDestinationQuery] = useState(destination.name || "");
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [sortBy, setSortBy] = useState("DEFAULT");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [durationFilters, setDurationFilters] = useState([]);
  const [ratingFilter, setRatingFilter] = useState(null);
  const [freeCancelOnly, setFreeCancelOnly] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);

  // Filter bar chip state
  const initialStart = params.get("start");
  const initialEnd = params.get("end");
  const [dateRange, setDateRange] = useState([
    initialStart ? new Date(initialStart) : null,
    initialEnd ? new Date(initialEnd) : null,
  ]);
  const [showDatePopover, setShowDatePopover] = useState(false);
  const [adults, setAdults] = useState(2);
  const [showAdultsPopover, setShowAdultsPopover] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(null, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination.destination_id]);

  const fetchProducts = async (overrideRange, page = 1) => {
    if (!destination.destination_id) {
      setLoading(false);
      setErrorMsg("No destination selected. Please start a new search.");
      return;
    }
    setLoading(true);
    setErrorMsg("");

    const range = overrideRange || dateRange;
    const filtering = { destination: String(destination.destination_id) };
    if (range[0] && range[1]) {
      filtering.startDate = formatDate(range[0]);
      filtering.endDate = formatDate(range[1]);
    }

    const payload = {
      language: "en-US",
      filtering,
      sorting: { sort: "DEFAULT" },
      pagination: { start: (page - 1) * PAGE_SIZE + 1, count: PAGE_SIZE },
      currency: destination.default_currency_code || "USD",
    };

    try {
      const response = await axios.post(`${base_url.api1}/tourapp/viator/products/search`, payload);
      if (response.data && response.data.status) {
        const viatorResponse = response.data.viator_response || {};
        const list = viatorResponse.products || [];
        list.forEach(cacheProduct);
        setProducts(list);
        setTotalCount(viatorResponse.totalCount || list.length);
      } else {
        setProducts([]);
        setTotalCount(0);
        setErrorMsg("No experiences found for this search.");
      }
    } catch (error) {
      console.error("Error searching experiences:", error);
      setProducts([]);
      setTotalCount(0);
      setErrorMsg("Something went wrong while searching. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page === currentPage || page < 1) return;
    setCurrentPage(page);
    fetchProducts(null, page);
    const grid = document.querySelector(".exp-product-grid");
    if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEditSearch = (e) => {
    e.preventDefault();
    navigate("/experiences");
  };

  const toggleDuration = (key) => {
    setDurationFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const clearFilters = () => {
    setMinPrice("");
    setMaxPrice("");
    setDurationFilters([]);
    setRatingFilter(null);
    setFreeCancelOnly(false);
  };

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (freeCancelOnly) {
      list = list.filter((p) => p.flags && p.flags.includes("FREE_CANCELLATION"));
    }

    if (ratingFilter) {
      list = list.filter((p) => {
        const r = getRating(p);
        return r !== null && r >= ratingFilter;
      });
    }

    if (durationFilters.length) {
      list = list.filter((p) => {
        const mins = p.duration && p.duration.fixedDurationInMinutes;
        return durationFilters.some((key) => {
          const opt = DURATION_OPTIONS.find((o) => o.key === key);
          return opt && opt.test(mins);
        });
      });
    }

    if (activeCategory) {
      const chip = CATEGORY_CHIPS.find((c) => c.key === activeCategory);
      if (chip) {
        list = list.filter((p) => {
          const haystack = `${p.title || ""} ${p.description || ""}`.toLowerCase();
          return chip.keywords.some((kw) => haystack.includes(kw));
        });
      }
    }

    const min = parseFloat(minPrice);
    const max = parseFloat(maxPrice);
    if (!Number.isNaN(min)) {
      list = list.filter((p) => {
        const price = getFromPrice(p);
        return price !== null && price >= min;
      });
    }
    if (!Number.isNaN(max)) {
      list = list.filter((p) => {
        const price = getFromPrice(p);
        return price !== null && price <= max;
      });
    }

    if (sortBy === "PRICE_LOW") {
      list.sort((a, b) => (getFromPrice(a) ?? Infinity) - (getFromPrice(b) ?? Infinity));
    } else if (sortBy === "PRICE_HIGH") {
      list.sort((a, b) => (getFromPrice(b) ?? -Infinity) - (getFromPrice(a) ?? -Infinity));
    } else if (sortBy === "RATING") {
      list.sort((a, b) => (getRating(b) ?? 0) - (getRating(a) ?? 0));
    }

    return list;
  }, [products, freeCancelOnly, ratingFilter, durationFilters, minPrice, maxPrice, sortBy, activeCategory]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleSelectProduct = (product) => {
    cacheProduct(product);
    navigate(`/experiences/details/${product.productCode}`, {
      state: { product, destination },
    });
  };

  return (
    <div className="exp-scope">
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div style={{ position: "relative" }}>
        <Nav />
      </div>

     <div className="exp-results-topbar">
  <div className="exp-container exp-results-topbar-inner">

    <form
      className="exp-mini-search"
      onSubmit={handleEditSearch}
    >
      {/* Destination */}
      <div
        className="exp-mini-field"
        onClick={handleEditSearch}
      >
        <div className="exp-location-icon">
          <PlaceIcon />
        </div>

        <div className="exp-location-content">
          <span className="exp-mini-label">
            Destination
          </span>

          <input
            type="text"
            readOnly
            value={destinationQuery}
            onChange={() => {}}
            placeholder="Where do you want to go?"
          />
        </div>
      </div>

      {/* Divider */}
      <div className="exp-search-divider"></div>

      {/* Edit Search */}
      <button
        type="submit"
        className="exp-mini-search-btn"
      >
        <SearchIcon />
        <span>Edit search</span>
      </button>
    </form>

  </div>
</div>

      <div className="exp-container">
        <div className="exp-results-body">
          <div className="exp-results-header">
            <div>
              <h1>
                {totalCount > 0
                  ? `${totalCount.toLocaleString()} experiences in ${destination.name}`
                  : `Experiences in ${destination.name}`}
              </h1>
              <p>Tours, tickets and activities you can book straight through Linsoa.</p>
            </div>
            <select
              className="exp-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="DEFAULT">Sort: Featured</option>
              <option value="PRICE_LOW">Price: Low to high</option>
              <option value="PRICE_HIGH">Price: High to low</option>
              <option value="RATING">Highest rated</option>
            </select>
          </div>

          <div className="exp-filterbar">
            <div className="exp-filterbar-chips">
              <Popover
                opened={showDatePopover}
                onChange={setShowDatePopover}
                position="bottom-start"
                shadow="md"
                withinPortal
              >
                <Popover.Target>
                  <button
                    type="button"
                    className="exp-filter-chip"
                    onClick={() => setShowDatePopover((o) => !o)}
                  >
                    <EventAvailableIcon fontSize="small" />
                    {dateRange[0] && dateRange[1]
                      ? formatDateRange(dateRange[0], dateRange[1])
                      : "Any dates"}
                  </button>
                </Popover.Target>
                <Popover.Dropdown>
                  <DatePicker
                    type="range"
                    value={dateRange}
                    onChange={setDateRange}
                    minDate={new Date()}
                    allowSingleDateInRange
                  />
                  <button
                    type="button"
                    className="exp-popover-apply"
                    onClick={() => {
                      setShowDatePopover(false);
                      setCurrentPage(1);
                      fetchProducts(dateRange, 1);
                    }}
                  >
                    Apply dates
                  </button>
                </Popover.Dropdown>
              </Popover>

              <Popover
                opened={showAdultsPopover}
                onChange={setShowAdultsPopover}
                position="bottom-start"
                shadow="md"
                withinPortal
              >
                <Popover.Target>
                  <button
                    type="button"
                    className="exp-filter-chip"
                    onClick={() => setShowAdultsPopover((o) => !o)}
                  >
                    <PersonOutlineIcon fontSize="small" />
                    {adults} {adults === 1 ? "Adult" : "Adults"}
                    <ExpandMoreIcon fontSize="small" />
                  </button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div className="exp-adults-stepper">
                    <span>Adults</span>
                    <div className="exp-stepper-controls">
                      <button
                        type="button"
                        onClick={() => setAdults((a) => Math.max(1, a - 1))}
                        aria-label="Decrease adults"
                      >
                        <RemoveIcon fontSize="small" />
                      </button>
                      <span>{adults}</span>
                      <button
                        type="button"
                        onClick={() => setAdults((a) => a + 1)}
                        aria-label="Increase adults"
                      >
                        <AddIcon fontSize="small" />
                      </button>
                    </div>
                  </div>
                </Popover.Dropdown>
              </Popover>

              <button
                type="button"
                className="exp-filter-chip exp-filter-chip-solid"
                onClick={() => setShowFiltersPanel(true)}
              >
                <TuneIcon fontSize="small" /> Filters
              </button>
            </div>

            <span className="exp-filterbar-divider" />

            <div className="exp-category-scroll">
              {CATEGORY_CHIPS.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className={`exp-category-pill${
                    activeCategory === chip.key ? " exp-category-pill-active" : ""
                  }`}
                  onClick={() =>
                    setActiveCategory((prev) => (prev === chip.key ? null : chip.key))
                  }
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>

          <div className="exp-results-count">
            {!loading && totalCount > 0 ? `${totalCount.toLocaleString()}+ results` : ""}
          </div>

          {showFiltersPanel && (
            <div
              className="exp-filters-modal-overlay"
              onClick={() => setShowFiltersPanel(false)}
            >
              <div className="exp-filters-modal" onClick={(e) => e.stopPropagation()}>
                <div className="exp-filters-modal-header">
                  <h3>Filters</h3>
                  <button
                    type="button"
                    className="exp-filters-modal-close"
                    onClick={() => setShowFiltersPanel(false)}
                    aria-label="Close filters"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>

                <div className="exp-filters-modal-body">
                  <div className="exp-filter-group">
                    <h4>Price per person</h4>
                    <div className="exp-price-inputs">
                      <input
                        type="number"
                        min="0"
                        placeholder="Min"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                      />
                      <span>-</span>
                      <input
                        type="number"
                        min="0"
                        placeholder="Max"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="exp-filter-group">
                    <h4>Duration</h4>
                    {DURATION_OPTIONS.map((opt) => (
                      <label className="exp-filter-option" key={opt.key}>
                        <input
                          type="checkbox"
                          checked={durationFilters.includes(opt.key)}
                          onChange={() => toggleDuration(opt.key)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>

                  <div className="exp-filter-group">
                    <h4>Traveler rating</h4>
                    {RATING_OPTIONS.map((r) => (
                      <label className="exp-filter-option" key={r}>
                        <input
                          type="checkbox"
                          checked={ratingFilter === r}
                          onChange={() => setRatingFilter(ratingFilter === r ? null : r)}
                        />
                        <span className="exp-filter-stars">
                          {Array.from({ length: r }).map((_, i) => (
                            <StarIcon key={i} fontSize="inherit" />
                          ))}
                        </span>
                        &amp; up
                      </label>
                    ))}
                  </div>

                  <div className="exp-filter-group">
                    <h4>Booking options</h4>
                    <label className="exp-filter-option">
                      <input
                        type="checkbox"
                        checked={freeCancelOnly}
                        onChange={() => setFreeCancelOnly((v) => !v)}
                      />
                      Free cancellation
                    </label>
                  </div>
                </div>

                <div className="exp-filters-modal-footer">
                  <button type="button" className="exp-clear-filters" onClick={clearFilters}>
                    Clear all
                  </button>
                  <button
                    type="button"
                    className="exp-apply-filters"
                    onClick={() => setShowFiltersPanel(false)}
                  >
                    Show results
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="exp-product-grid">
            {loading && (
              <div className="exp-loading-grid">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div className="exp-skeleton-card" key={i}>
                    <div className="exp-skeleton-img" />
                    <div className="exp-skeleton-line" style={{ width: "80%" }} />
                    <div className="exp-skeleton-line" style={{ width: "50%" }} />
                  </div>
                ))}
              </div>
            )}

            {!loading && filteredProducts.length === 0 && (
              <div className="exp-empty-state">
                <h3>No experiences match your filters</h3>
                <p>{errorMsg || "Try widening your price range or clearing a filter."}</p>
              </div>
            )}

            {!loading &&
              filteredProducts.map((product) => (
                <ExperienceProductCard
                  key={product.productCode}
                  product={product}
                  onSelect={handleSelectProduct}
                />
              ))}
          </div>

          {!loading && totalPages > 1 && (
            <nav className="exp-pagination" aria-label="Results pagination">
              <button
                type="button"
                className="exp-pagination-arrow"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <ChevronLeftIcon fontSize="small" />
              </button>

              {buildPageList(currentPage, totalPages).map((item, idx) =>
                item === "..." ? (
                  <span className="exp-pagination-ellipsis" key={`ellipsis-${idx}`}>
                    ...
                  </span>
                ) : (
                  <button
                    type="button"
                    key={item}
                    className={`exp-pagination-number${
                      item === currentPage ? " exp-pagination-number-active" : ""
                    }`}
                    onClick={() => handlePageChange(item)}
                    aria-current={item === currentPage ? "page" : undefined}
                  >
                    {item}
                  </button>
                )
              )}

              <button
                type="button"
                className="exp-pagination-arrow"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <ChevronRightIcon fontSize="small" />
              </button>
            </nav>
          )}
        </div>
      </div>

      <FooterNew />
    </div>
  );
}
